import { useState, useMemo, useRef } from "react";
import { ArrowLeft, Search, ExternalLink } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { useTenant } from "../../contexts/TenantContext";
import { useHelpPanel } from "../../contexts/HelpPanelContext";
import { ALL_GUIDES, getGuideById } from "./data";
import GuideCard from "./components/GuideCard";
import GuideRenderer from "./GuideRenderer";
import TableOfContents from "./TableOfContents";

export default function HelpCenter() {
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [search, setSearch] = useState("");
  const tenant = useTenant();
  const { openGuide } = useHelpPanel();
  const scrollRef = useRef(null);

  const tenantFeatures = tenant.features || {};

  // Filter guides by tenant features and search
  const visibleGuides = useMemo(() => {
    return ALL_GUIDES.filter((guide) => {
      // Feature gate
      if (guide.requires && guide.requires.length > 0) {
        const hasAll = guide.requires.every((feat) => tenantFeatures[feat]);
        if (!hasAll) return false;
      }
      // Search filter
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchTitle = guide.title.toLowerCase().includes(q);
        const matchDesc = guide.description.toLowerCase().includes(q);
        const matchSections = guide.sections.some((s) =>
          s.title.toLowerCase().includes(q),
        );
        return matchTitle || matchDesc || matchSections;
      }
      return true;
    });
  }, [search, tenantFeatures]);

  const selectedGuide = selectedGuideId ? getGuideById(selectedGuideId) : null;

  // Guide viewer
  if (selectedGuide) {
    return (
      <div>
        <button
          onClick={() => setSelectedGuideId(null)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 0",
            marginBottom: 16,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 14,
            color: "#666",
          }}
        >
          <ArrowLeft size={16} />
          Back to guides
        </button>

        <h1
          style={{
            fontFamily: fonts.heading,
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          {selectedGuide.title}
        </h1>
        <p style={{ color: "#666", marginBottom: 24, fontSize: 14 }}>
          {selectedGuide.description}
        </p>

        <button
          onClick={() => {
            openGuide(selectedGuide.id);
            setSelectedGuideId(null);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            borderRadius: 6,
            border: `1px solid ${colors.accent}`,
            background: colors.accentDim,
            color: colors.accent,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: fonts.body,
            cursor: "pointer",
            marginBottom: 20,
          }}
        >
          <ExternalLink size={14} />
          Follow Along
        </button>

        <div style={{ display: "flex", gap: 32 }}>
          {/* TOC sidebar */}
          <div style={{ width: 220, flexShrink: 0 }}>
            <TableOfContents
              sections={selectedGuide.sections}
              scrollContainer={scrollRef}
            />
          </div>

          {/* Guide content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <GuideRenderer guide={selectedGuide} />
          </div>
        </div>
      </div>
    );
  }

  // Guide index
  return (
    <div>
      <h1
        style={{
          fontFamily: fonts.heading,
          fontSize: 28,
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        Help & Guides
      </h1>
      <p style={{ color: "#666", marginBottom: 24 }}>
        Step-by-step guides and documentation for the Insights Platform.
      </p>

      {/* Search */}
      <div
        style={{
          position: "relative",
          maxWidth: 400,
          marginBottom: 24,
        }}
      >
        <Search
          size={16}
          color="#999"
          style={{ position: "absolute", left: 12, top: 10 }}
        />
        <input
          type="text"
          placeholder="Search guides..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px 8px 36px",
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Guide cards grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 20,
        }}
      >
        {visibleGuides.map((guide) => (
          <GuideCard
            key={guide.id}
            guide={guide}
            onClick={() => setSelectedGuideId(guide.id)}
            onFollowAlong={() => openGuide(guide.id)}
          />
        ))}
      </div>

      {visibleGuides.length === 0 && (
        <p style={{ color: "#999", textAlign: "center", padding: 40 }}>
          No guides found{search ? ` matching "${search}"` : ""}.
        </p>
      )}
    </div>
  );
}
